import { useMemo, useState } from 'react';

import { SpendChartCard } from '@apps/web/components/admin/llm_spend/chart_shared';
import {
    COMPONENT_SERIES,
    type SpendBucketSize,
} from '@apps/web/components/admin/llm_spend/constants';
import { SpendTotalsTable } from '@apps/web/components/admin/llm_spend/spend_totals_table';
import {
    StackedSpendChart,
    type StackedValueMode,
} from '@apps/web/components/admin/llm_spend/stacked_spend_chart';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';

type ComponentBucket = {
    readonly bucket_start: string;
    readonly spend_by_component: Readonly<Record<string, number>>;
    readonly spend_by_model_component: Readonly<
        Record<string, Readonly<Record<string, number>>>
    >;
};

const VALUE_MODE_OPTIONS = [
    { value: 'usd' as const, label: '$', ariaLabel: 'Dollars' },
    { value: 'share' as const, label: '%', ariaLabel: 'Share of total' },
];

export function ModelComponentSpendCard({
    buckets,
    bucket,
    title = 'Cost components',
}: {
    readonly buckets: ReadonlyArray<ComponentBucket>;
    readonly bucket: SpendBucketSize;
    readonly title?: string;
}) {
    const models = useMemo(() => {
        const totals = new Map<string, number>();
        for (const row of buckets) {
            for (const [model, components] of Object.entries(
                row.spend_by_model_component,
            )) {
                const total = Object.values(components).reduce(
                    (sum, value) => sum + value,
                    0,
                );
                totals.set(model, (totals.get(model) ?? 0) + total);
            }
        }
        return [...totals]
            .sort((left, right) => right[1] - left[1])
            .map(([model]) => model);
    }, [buckets]);
    const [requestedModel, setRequestedModel] = useState('overall');
    const selectedModel =
        requestedModel === 'overall' || models.includes(requestedModel)
            ? requestedModel
            : 'overall';
    const [valueMode, setValueMode] = useState<StackedValueMode>('usd');
    const points = buckets.map((row) => ({
        bucketStart: row.bucket_start,
        values:
            selectedModel === 'overall'
                ? row.spend_by_component
                : (row.spend_by_model_component[selectedModel] ?? {}),
    }));

    return (
        <SpendChartCard
            title={title}
            action={
                <div className="flex items-center gap-2">
                    <Select
                        value={selectedModel}
                        onValueChange={setRequestedModel}
                    >
                        <SelectTrigger
                            size="sm"
                            className="max-w-56 min-w-40"
                            aria-label={`${title}: model`}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="overall">All models</SelectItem>
                            {models.map((model) => (
                                <SelectItem key={model} value={model}>
                                    {model}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <SegmentedControl
                        options={VALUE_MODE_OPTIONS}
                        value={valueMode}
                        onValueChange={setValueMode}
                        size="xs"
                        layout="auto"
                        aria-label={`${title}: dollars or share of total`}
                    />
                </div>
            }
        >
            <StackedSpendChart
                points={points}
                series={COMPONENT_SERIES}
                bucket={bucket}
                valueMode={valueMode}
            />
            <SpendTotalsTable
                dimensionLabel="Component"
                points={points}
                series={COMPONENT_SERIES}
            />
        </SpendChartCard>
    );
}
